import { CrownCapNames } from './CrownCapNames';
import { CrownPointsMaterial, CrownPointsMaterialColorAttribute, CrownPoinsMaterialColorMode } from './CrownPointsMaterial';
import { PointCloud, ColorMaps, MaterialCache, PointsMaterialShape, Orientation } from '@sick-davinci/glow';
import { CrownAccessorBuilder } from '@sick-davinci/device-communication';
import { Points } from 'three';
/**
 * Capability to draw pointclouds in various manners (colormaps, shapes, sizes)
 */
export class CrownPointCloudCap {
    constructor() {
        this._materialCache = new MaterialCache();
        this._pointShape = PointsMaterialShape.circle;
        this._sizeAttenuation = true;
    }
    setPointShape(shape) {
        this._pointShape = shape;
    }
    setSizeAttenuation(attenuation) {
        this._sizeAttenuation = attenuation;
    }
    getName() {
        return CrownCapNames.CROWNPOINTCLOUD;
    }
    doClassToCapabilityMapping(map) {
        map.set(CrownPointCloudCap.className, CrownCapNames.CROWNPOINTCLOUD);
        map.set(CrownPointCloudCap.decoClassName, CrownCapNames.CROWNPOINTCLOUD);
    }
    call(objectIn, sceneIn) {
        const { payload } = objectIn;
        if (payload.Type === CrownPointCloudCap.className) {
            const decoration = payload.Decorations != null && payload.Decorations.length === 1 ? payload.Decorations[0].data : null;
            payload.Iconics.forEach((pc) => {
                const p = pc.data.Points;
                if (this._isStructureDifferent(p)) {
                    this._accessor = new CrownAccessorBuilder(p.endian === 'little', p.elemTypes, ['x', 'y', 'z', 'i']).build();
                    this._endian = p.endian;
                    this._elemTypes = p.elemTypes;
                }
                this._accessor.init(p.data.buffer.slice(p.data.byteOffset, p.elemSz * p.numOfElems + p.data.byteOffset), p.numOfElems);
                const geometry = PointCloud.createFromAccessor(this._accessor);
                const material = this._createMaterial(decoration);
                const points = new Points(geometry, material);
                points.name = objectIn.name;
                Orientation.set(points, objectIn);
                sceneIn.addToParent(objectIn.parent || '', points);
                sceneIn.setTag(objectIn.tag, points);
            });
            return true;
        }
        return false;
    }
    update(objectIn, sceneIn) {
        if (objectIn.payload.IconicId != null) {
            const { payload: { Decoration, IconicId } } = objectIn;
            if (Decoration.class === CrownPointCloudCap.decoClassName) {
                const pc = sceneIn.getObjectByName(IconicId);
                if (pc != null) {
                    pc.material = this._createMaterial(Decoration.data);
                    pc.material.needsUpdate = true;
                    return true;
                }
            }
        }
        return false;
    }
    _isStructureDifferent(points) {
        if (this._accessor) {
            if (points.endian === this._endian) {
                let i = points.elemTypes.length;
                if (i === this._elemTypes.length) {
                    while (i--) {
                        if (points.elemTypes[i] !== this._elemTypes[i]) {
                            return true;
                        }
                    }
                    return false;
                }
            }
        }
        return true;
    }
    _createMaterial(decoration) {
        const materialParameter = {
            decoration,
            pointShape: this._pointShape,
            sizeAttenuation: this._sizeAttenuation,
        };
        return this._materialCache.getMaterial(materialParameter, () => {
            let min;
            let max;
            let colorMapIndex;
            let colorAttribute;
            if (decoration.XRangeSet) {
                min = decoration.XMin;
                max = decoration.XMax;
                colorMapIndex = decoration.XColorMap;
                colorAttribute = CrownPointsMaterialColorAttribute.X;
            }
            else if (decoration.YRangeSet) {
                min = decoration.YMin;
                max = decoration.YMax;
                colorMapIndex = decoration.YColorMap;
                colorAttribute = CrownPointsMaterialColorAttribute.Y;
            }
            else if (decoration.ZRangeSet) {
                min = decoration.ZMin;
                max = decoration.ZMax;
                colorMapIndex = decoration.ZColorMap;
                colorAttribute = CrownPointsMaterialColorAttribute.Z;
            }
            else {
                min = decoration.IMin;
                max = decoration.IMax;
                colorMapIndex = decoration.IColorMap;
                colorAttribute = CrownPointsMaterialColorAttribute.PRELIMARY_COLOR;
            }
            const colorMap = ColorMaps.getModelByIndex(colorMapIndex);
            return new CrownPointsMaterial({
                size: decoration.PointSize,
                sizeAttenuation: this._sizeAttenuation,
                pointsShape: this._pointShape,
                colorAttribute,
                interpolationMode: CrownPoinsMaterialColorMode.LINEAR_INTERPOLATION,
            }).setColorMap(colorMap, min, max);
        });
    }
}
CrownPointCloudCap.className = 'POINT_CLOUD';
CrownPointCloudCap.decoClassName = 'View.PointCloudDecoration';
//# sourceMappingURL=CrownPointCloudCap.js.map